import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export interface FormTemplate {
	id: string;
	form_code: string;
	form_name: string;
	form_description: string;
	jurisdiction: string;
	form_type: string;
	form_category: string;
	applicable_device_classes: string[];
	required_fields: Array<{
		name: string;
		type: string;
		label: string;
		max_length?: number;
		options?: string[];
	}>;
	optional_fields: Array<{
		name: string;
		type: string;
		label: string;
		max_length?: number;
	}>;
	supporting_documents: string[];
	file_format?: string;
	file_size?: number;
	version?: string;
	is_current?: boolean;
	is_template?: boolean;
	s3_url?: string;
	s3_key?: string; // S3 key for the uploaded form file
	original_url?: string;
	source_page_url?: string;
	site_url?: string; // Homepage URL of the official website where this form is found
	has_checklist?: boolean; // Whether form has a generic checklist document uploaded
	checklist_s3_url?: string; // S3 URL for generic checklist document
	md_s3_url?: string; // S3 URL for Medical Device (MD) specific version
	md_s3_key?: string; // S3 key for MD version
	ivd_s3_url?: string; // S3 URL for In Vitro Diagnostic (IVD) specific version
	ivd_s3_key?: string; // S3 key for IVD version
	has_md_checklist?: boolean; // Whether form has MD-specific checklist uploaded
	md_checklist_s3_url?: string; // S3 URL for MD checklist document
	has_ivd_checklist?: boolean; // Whether form has IVD-specific checklist uploaded
	ivd_checklist_s3_url?: string; // S3 URL for IVD checklist document
}

export interface PaginatedFormsResponse {
	forms: FormTemplate[];
	pagination: {
		page: number;
		limit: number;
		total: number;
		pages: number;
		has_next: boolean;
		has_prev: boolean;
	};
	available_jurisdictions?: string[];
}

export interface DownloadResponse {
	content: string; // Base64 encoded content
	filename: string;
	content_type: string;
}

class FormService {
	private getAuthHeaders() {
		const token = localStorage.getItem("gamora_token");
		return {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		};
	}

	async getFormTemplates(
		page: number = 1,
		limit: number = 10,
		jurisdiction?: string,
		deviceClass?: string,
		formType?: string
	): Promise<PaginatedFormsResponse> {
		try {
			const params = new URLSearchParams({
				page: page.toString(),
				limit: limit.toString(),
			});

			if (jurisdiction && jurisdiction !== "all") {
				params.append("jurisdiction", jurisdiction);
			}
			if (deviceClass && deviceClass !== "all") {
				params.append("device_class", deviceClass);
			}
			if (formType && formType !== "all") {
				params.append("form_type", formType);
			}

			// Use new forms endpoint that returns forms from the forms table
			const response = await axios.get(
				`${API_BASE_URL}/api/v1/forms?${params.toString()}`,
				{
					headers: this.getAuthHeaders(),
				}
			);
			return response.data;
		} catch (error) {
			console.error("Error fetching form templates:", error);
			throw error;
		}
	}

	async downloadFormTemplate(templateId: string): Promise<void> {
		try {
			// Try to get form details first to check for S3 URL, original_url, or source_page_url
			try {
				const formResponse = await axios.get(
					`${API_BASE_URL}/api/v1/forms`,
					{
						headers: this.getAuthHeaders(),
						params: { page: 1, limit: 100 } // Get forms to find this one
					}
				);
				
				const forms = formResponse.data.forms || [];
				const form = forms.find((f: FormTemplate) => f.id === templateId);
				
				// Priority: s3_url > original_url > source_page_url
				if (form && (form.s3_url || form.original_url || form.source_page_url)) {
					// Use direct URL - browser will handle download
					const downloadUrl = form.s3_url || form.original_url || form.source_page_url;
					if (downloadUrl) {
						window.open(downloadUrl, '_blank');
						return;
					}
				}
			} catch (err) {
				// If we can't get form details, continue to download endpoint
				console.warn("Could not get form details, trying download endpoint:", err);
			}
			
			// Fallback: Use download endpoint (handles redirects and fallback to source_page_url)
			window.location.href = `${API_BASE_URL}/api/v1/forms/${templateId}/download`;
		} catch (error) {
			console.error("Error downloading form template:", error);
			throw error;
		}
	}

	async downloadForm(formId: string): Promise<DownloadResponse> {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/api/v1/compliance/forms/${formId}/download`,
				{
					headers: this.getAuthHeaders(),
					responseType: "blob",
				}
			);

			// Convert blob to base64
			const reader = new FileReader();
			return new Promise((resolve, reject) => {
				reader.onload = () => {
					const base64 = reader.result as string;
					resolve({
						content: base64.split(",")[1], // Remove data:application/pdf;base64, prefix
						filename: formId + ".pdf",
						content_type: "application/pdf",
					});
				};
				reader.onerror = reject;
				reader.readAsDataURL(response.data);
			});
		} catch (error) {
			console.error("Error downloading form:", error);
			throw error;
		}
	}

	async uploadFormFile(formId: string, file: File): Promise<{status: string; message: string; s3_url?: string; s3_key?: string}> {
		try {
			const formData = new FormData();
			formData.append("file", file);

			const response = await axios.post(
				`${API_BASE_URL}/api/v1/forms/${formId}/upload`,
				formData,
				{
					headers: {
						...this.getAuthHeaders(),
						"Content-Type": "multipart/form-data",
					},
				}
			);

			return response.data;
		} catch (error: any) {
			console.error("Error uploading form file:", error);
			if (error.response?.data?.detail) {
				throw new Error(error.response.data.detail);
			}
			throw new Error("Failed to upload form file");
		}
	}

	async uploadDeviceClassFormFile(formId: string, file: File, deviceClass: 'md' | 'ivd'): Promise<{status: string; message: string; s3_url?: string; s3_key?: string}> {
		try {
			const formData = new FormData();
			formData.append("file", file);

			const response = await axios.post(
				`${API_BASE_URL}/api/v1/forms/${formId}/upload/${deviceClass}`,
				formData,
				{
					headers: {
						...this.getAuthHeaders(),
						"Content-Type": "multipart/form-data",
					},
				}
			);

			return response.data;
		} catch (error: any) {
			console.error(`Error uploading ${deviceClass} form file:`, error);
			if (error.response?.data?.detail) {
				throw new Error(error.response.data.detail);
			}
			throw new Error(`Failed to upload ${deviceClass} form file`);
		}
	}

	async uploadChecklistDocument(formId: string, file: File): Promise<{status: string; message: string; checklist_s3_url?: string; chunks_created?: number}> {
		try {
			const formData = new FormData();
			formData.append("file", file);

			const response = await axios.post(
				`${API_BASE_URL}/api/v1/forms/${formId}/checklist/upload`,
				formData,
				{
					headers: {
						...this.getAuthHeaders(),
						"Content-Type": "multipart/form-data",
					},
				}
			);

			return response.data;
		} catch (error: any) {
			console.error("Error uploading checklist document:", error);
			if (error.response?.data?.detail) {
				throw new Error(error.response.data.detail);
			}
			throw new Error("Failed to upload checklist document");
		}
	}

	async uploadDeviceClassChecklistDocument(formId: string, file: File, deviceClass: 'md' | 'ivd'): Promise<{status: string; message: string; checklist_s3_url?: string; chunks_created?: number; device_class?: string}> {
		try {
			const formData = new FormData();
			formData.append("file", file);

			const response = await axios.post(
				`${API_BASE_URL}/api/v1/forms/${formId}/checklist/upload/${deviceClass}`,
				formData,
				{
					headers: {
						...this.getAuthHeaders(),
						"Content-Type": "multipart/form-data",
					},
				}
			);

			return response.data;
		} catch (error: any) {
			console.error(`Error uploading ${deviceClass} checklist document:`, error);
			if (error.response?.data?.detail) {
				throw new Error(error.response.data.detail);
			}
			throw new Error(`Failed to upload ${deviceClass} checklist document`);
		}
	}

	// Helper method to trigger download
	triggerDownload(content: string, filename: string, contentType: string) {
		const byteCharacters = atob(content);
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);
		const blob = new Blob([byteArray], { type: contentType });

		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		window.URL.revokeObjectURL(url);
		document.body.removeChild(a);
	}
}

export const formService = new FormService();
